import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ReferenceLine } from "recharts";
import { Coins } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading, Kpi } from "@/components/common";
import { inr, inrL, num } from "@/lib/format";

const UCOLORS = { Refrigeration: "#2563eb", Steam: "#f59e0b", Electricity: "#8b5cf6", Water: "#06b6d4", "Compressed Air": "#10b981", "Total Utility": "#0f172a" };

export default function UtilityEconomics() {
  const { data } = usePoll(endpoints.economics, 5000);
  if (!data) return <Loading />;

  // Build waterfall (transparent base + visible value)
  const wf = data.waterfall.map((w) => ({
    name: w.name,
    base: w.total ? 0 : w.start,
    value: w.value,
    total: !!w.total,
  }));

  return (
    <div data-testid="utility-economics-page">
      <PageHeader icon={Coins} title="Utility Economics Center"
        subtitle="The differentiator — true ₹/litre built up from every utility, traceable to process, line & product." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi testid="eco-total" label="Total Utility" value={inrL(data.metrics.utility_per_l)} unit="/L" accent="#0f172a" />
        <Kpi testid="eco-hr" label="Utility Spend" value={inr(data.metrics.total_per_hr)} unit="/hr" accent="#10b981" />
        {data.breakdown.slice(0, 2).map((b) => (
          <Kpi key={b.name} label={b.name} value={inrL(b.inr_per_l)} unit="/L" sub={`${b.share_pct}% share`} accent={UCOLORS[b.name]} />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <Panel testid="panel-waterfall" title="Cost Build-up Waterfall" subtitle="₹/L accumulation by utility">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={wf} margin={{ top: 10, right: 10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n) => (n === "value" ? inrL(v) + "/L" : null)} cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="base" stackId="a" fill="transparent" isAnimationActive={false} />
              <Bar dataKey="value" stackId="a" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {wf.map((w, i) => <Cell key={i} fill={w.total ? "#0f172a" : UCOLORS[w.name] || "#64748b"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="panel-breakdown-table" title="Utility Breakdown" subtitle="Allocation by utility system">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="text-left font-semibold py-2">Utility</th>
                <th className="text-right font-semibold">₹/hr</th>
                <th className="text-right font-semibold">₹/L</th>
                <th className="text-right font-semibold">Share</th>
              </tr>
            </thead>
            <tbody>
              {data.breakdown.map((b) => (
                <tr key={b.name} className="border-b border-slate-50" data-testid={`breakdown-${b.key}`}>
                  <td className="py-2.5 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: UCOLORS[b.name] }} />
                    {b.name}
                  </td>
                  <td className="text-right font-mono">{num(b.cost_per_hr)}</td>
                  <td className="text-right font-mono font-semibold text-emerald-600">{inrL(b.inr_per_l)}</td>
                  <td className="text-right font-mono text-slate-500">{b.share_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>

      <Panel testid="panel-line-drilldown" title="Drilldown — Cost by Production Line" subtitle="Utility cost attribution across product lines">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="text-left font-semibold py-2">Production Line</th>
              <th className="text-right font-semibold">Utility ₹/hr</th>
              <th className="text-right font-semibold">Share</th>
              <th className="text-right font-semibold">Utility ₹/L</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((l) => (
              <tr key={l.line} className="border-b border-slate-50">
                <td className="py-2.5 font-medium text-slate-800">{l.line}</td>
                <td className="text-right font-mono">{num(l.utility_cost_per_hr)}</td>
                <td className="text-right font-mono text-slate-500">{l.share_pct}%</td>
                <td className="text-right font-mono font-semibold text-emerald-600">{inrL(l.utility_per_l)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
